import { useQuery } from '@tanstack/react-query';

import { todayScheduleFetcher } from '../../fetchers/batches-fetcher';

type Props = {
  data: TodaySchedule[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
};

const useTodaySchedule = ({ batchSlug }: { batchSlug: string }): Props => {
  const { data, isLoading, isError, error } = useQuery(
    [
      `today-schedule-${batchSlug}`,
      {
        batchSlug,
      },
    ],
    todayScheduleFetcher,
    {
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  let todaySchedule = data?.data;

  if (todaySchedule?.length > 0) {
    todaySchedule = todaySchedule.map((res: any) => new TodaySchedule(res));
  } else {
    todaySchedule = [];
  }

  return {
    data: todaySchedule,
    isLoading,
    isError,
    error,
  };
};

export class SubjectId {
  _id: string;
  name: string;
  slug: string;

  constructor(data: any) {
    data = data || {};
    this._id = data._id || '';
    this.name = data.name || '';
    this.slug = data.slug || '';
  }
}

export class VideoDetails {
  name: string;
  image: string;
  embedCode: string;
  description: string;
  duration: string;
  _id: string;
  id: string;

  constructor(data: any) {
    data = data || {};
    this.name = data.name || '';
    this.image = data.image || '';
    this.embedCode = data.embedCode || '';
    this.description = data.description || '';
    this.duration = data.duration || '';
    this._id = data._id || '';
    this.id = data.id || '';
  }
}

export class AttachmentId {
  baseUrl: string;
  _id: string;
  name: string;
  key: string;
  organization: string;
  createdAt: string;
  url: string;

  constructor(data: any) {
    data = data || {};
    this.baseUrl = data.baseUrl || '';
    this._id = data._id || '';
    this.name = data.name || '';
    this.key = data.key || '';
    this.organization = data.organization || '';
    this.createdAt = data.createdAt || '';
    this.url = this.baseUrl + this.key;
  }
}

export class HomeworkId {
  attachmentIds: Array<AttachmentId> = [];
  _id: string;
  topic: string;
  note: string;
  submissionDate: string;
  status: string;
  actions: Array<string> = [];
  isFree: boolean | false;

  constructor(data: any) {
    data = data || {};
    this._id = data._id || '';
    this.topic = data.topic || '';
    this.note = data.note || '';
    if (data && data.attachmentIds && data.attachmentIds.length > 0) {
      const list: Array<AttachmentId> = [];
      data.attachmentIds.forEach((el: any) => {
        list.push(new AttachmentId(el));
      });
      this.attachmentIds = [...list];
    }

    if (data && data.isFree) {
      this.isFree = data.isFree;
    } else {
      this.isFree = false;
    }
    this.submissionDate = data.submissionDate || '';
    this.status = data.status || '';
    this.actions = data.actions;
  }
}

export class Tag {
  _id: string;
  name: string;

  constructor(tag: any) {
    tag = tag || {};
    this._id = tag._id || '';
    this.name = tag.name || '';
  }
}

export class TodaySchedule {
  _id: string;
  restrictedSchedule: boolean;
  restrictedTime: number;
  urlType: string;
  exerciseIds: any[];
  homeworkIds: HomeworkId[];
  tags: Tag[];
  isActive: boolean;
  isFree: boolean;
  isChatEnabled: boolean;
  isDoubtEnabled: boolean;
  isCommentDisabled: boolean;
  status: string;
  batchSubjectId: string;
  day: string;
  date: string;
  startTime: string;
  endTime: string;
  timeline: any[];
  isDPPNotes: boolean;
  isDPPVideos: boolean;
  isPathshala: boolean;
  teachers: any[];
  topic: string;
  meetingId: string;
  meetingPassword: string;
  url: string;
  ratingCount: number;
  subjectId: SubjectId;
  isBatchDoubtEnabled: boolean;
  dRoomId: string;
  videoDetails: VideoDetails;
  isLive: boolean;
  conversationId: string;
  typeId: string;
  lastWatchedPointInSec: number;
  slug: string;
  scheduleId: string;

  constructor(data: any) {
    data = data || {};
    this.conversationId = data.conversationId || '';
    this._id = data._id || '';
    this.restrictedSchedule = data.restrictedSchedule || false;
    this.restrictedTime = data.restrictedTime || 0;
    this.urlType = data.urlType || '';
    this.exerciseIds = data.exerciseIds;
    if (data && data.homeworkIds && data.homeworkIds.length > 0) {
      const list: Array<HomeworkId> = [];
      data.homeworkIds.forEach((el: any) => {
        list.push(new HomeworkId(el));
      });
      this.homeworkIds = [...list];
    }
    if (data && data.tags && data.tags.length > 0) {
      const list: Array<Tag> = [];
      data.tags.forEach((el: any) => {
        list.push(new Tag(el));
      });
      this.tags = [...list];
    }
    this.isFree = data.isFree || false;
    this.isChatEnabled = data.isChatEnabled || false;
    this.isDoubtEnabled = data.isDoubtEnabled || false;
    this.isCommentDisabled = data.isCommentDisabled || false;
    this.status = data.status || '';
    this.batchSubjectId = data.batchSubjectId || '';
    this.day = data.data || '';
    this.date = data.date || '';
    this.startTime = data.startTime || '';
    this.endTime = data.endTime || '';
    this.timeline = data.timeline || [];
    this.isDPPNotes = data.isDPPNotes || false;
    this.isDPPVideos = data.isDPPVideos || false;
    this.isPathshala = data.isPathshala || false;
    this.teachers = data.teachers || [];
    this.topic = data.topic || '';
    this.meetingId = data.meetingId || '';
    this.meetingPassword = data.meetingPassword || '';
    this.url = data.url || '';
    this.isActive = data.isActive || false;
    this.videoDetails = data.videoDetails || {};
    this.typeId = data.typeId || '';
    this.isLive = data.isLive || false;
    this.subjectId = data.subjectId || {};
    this.dRoomId = data.dRoomId || '';
    this.lastWatchedPointInSec = data.lastWatchedPointInSec || 0;
    this.slug = data.slug || '';
    this.scheduleId = data.scheduleId || '';
    this.ratingCount = data.ratingCount || 0;
    this.isBatchDoubtEnabled = data.isBatchDoubtEnabled || false;

    if (data.homeworkIds.length > 0) {
      this.homeworkIds = data.homeworkIds.map((item: any) => new HomeworkId(item));
    } else {
      this.homeworkIds = [];
    }

    if (data.tags.length > 0) {
      this.tags = data?.tags?.map((item: any) => new HomeworkId(item));
    } else {
      this.tags = [];
    }
  }
}

export default useTodaySchedule;
